import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Autocomplete, FormControl, InputLabel, Select, MenuItem, CircularProgress, Checkbox } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import CloseIcon from '@mui/icons-material/Close';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import TextFieldWithMath from './TextFieldWithMath';
import { Document, Page, pdfjs } from 'react-pdf';
import HiddenPDF from './HiddenPDF';
import 'mathlive';
import { CoordinateGridAdmin, defaultCoordinateConfig } from '../../components/coordinateGrid';
import { TableAdmin, TableAnswerLogicSummary, defaultTableConfig, extractAnswerFromConfig } from '../../components/tableAnswer';
import { NumberLineAdmin, defaultNumberLineConfig } from '../../components/numberLine';
import { MappingAdmin, defaultMappingConfig, stringifyAnswer as stringifyMappingAnswer } from '../../components/mappingAnswer';
import { PolynomialGraphAdmin, defaultPolynomialGraphConfig } from '../../components/polynomialGraph';
import { RationalGraphAdmin, defaultRationalGraphConfig } from '../../components/rationalGraph';
import { TrigGraphAdmin, defaultTrigGraphConfig } from '../../components/trigGraph';
import { MultilineAdmin, defaultMultilineConfig } from '../../components/multiline';
import {
    defaultMathliveConfig,
    MATHLIVE_ANSWER_TYPES,
    stringifyMathliveAnswer,
    wrapLatexDelimiters,
    hasLatexDelimiters,
    MathAnswerSpecEditor,
} from '../../components/mathlive';
import { GRADING_TYPES, ANSWER_TOOLS } from '../../utils/hwConstants';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const HwQuestionForm = ({
    textbook = null,
    page = 0,
    numPages = null,
    textbooks = [],
    showTextbookSelector = false,
    showPageSelector = false,
    onSubmit,
    onCancel,
    compact = false,
    showPDF = false
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // State management
    const [hwQuestion, setHwQuestion] = useState({
        textbook: textbook,
        page: page,
        question_type: null,
        question_label: "",
        content: "",
        passage: "",
        options: [],
        answers: [],
        answer_tool: "",
        grading_type: null,
    });

    const [chosenTextbook, setChosenTextbook] = useState(textbook);
    const [selectedPage, setSelectedPage] = useState(page);
    const [questionLabel, setQuestionLabel] = useState("");
    const [chosenQuestionType, setChosenQuestionType] = useState(null);
    const [options, setOptions] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [chosenTool, setChosenTool] = useState("");
    const [numAnswers, setNumAnswers] = useState(1);
    const [gradingType, setGradingType] = useState(null);

    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfNumPages, setPdfNumPages] = useState(numPages);
    const [submitting, setSubmitting] = useState(false);

    // Coordinate grid state
    const [coordinateConfig, setCoordinateConfig] = useState(defaultCoordinateConfig);
    const [coordinateAnswer, setCoordinateAnswer] = useState({ elements: [] });

    // Table answer state
    const [tableConfig, setTableConfig] = useState(defaultTableConfig);
    const [tableAnswer, setTableAnswer] = useState({ rows: [] });

    // Number line state
    const [numberLineConfig, setNumberLineConfig] = useState(defaultNumberLineConfig);
    const [numberLineAnswer, setNumberLineAnswer] = useState({ elements: [] });

    // Mapping answer state
    const [mappingConfig, setMappingConfig] = useState(defaultMappingConfig);
    const [mappingAnswer, setMappingAnswer] = useState('');

    // Polynomial graph state
    const [polynomialGraphConfig, setPolynomialGraphConfig] = useState(defaultPolynomialGraphConfig);
    const [polynomialGraphAnswer, setPolynomialGraphAnswer] = useState({ correctGraphIndex: 0 });

    // Rational graph state
    const [rationalGraphConfig, setRationalGraphConfig] = useState(defaultRationalGraphConfig);
    const [rationalGraphAnswer, setRationalGraphAnswer] = useState({ correctGraphIndex: 0 });

    // Trig graph state
    const [trigGraphConfig, setTrigGraphConfig] = useState(defaultTrigGraphConfig);
    const [trigGraphAnswer, setTrigGraphAnswer] = useState({ correctGraphIndex: 0 });

    // Multiline state
    const [multilineConfig, setMultilineConfig] = useState(defaultMultilineConfig);
    const [multilineAnswer, setMultilineAnswer] = useState({ rows: [] });

    // Mathlive state — answer_config stores `{ allowTextInput }`.
    // `mathliveAnswerTypes[i]` tracks whether `hwQuestion.answers[i]` was
    // authored as a math expression or a text alternative. Index-aligned with
    // `hwQuestion.answers`. Only consulted when allowTextInput is on.
    const [mathliveConfig, setMathliveConfig] = useState(defaultMathliveConfig);
    const [mathliveAnswerTypes, setMathliveAnswerTypes] = useState([]);

    // Render as LaTeX checkbox state
    const [renderAsLatex, setRenderAsLatex] = useState(false);

    // Memoize pdf.js options to prevent re-triggering Document load on every render.
    // disableAutoFetch stops pdf.js from pre-downloading all pages after the first loads.
    const pdfOptions = React.useMemo(() => ({
        disableAutoFetch: true,
        disableStream: false,
    }), []);

    // Update hwQuestion when props change
    useEffect(() => {
        setChosenTextbook(textbook);
        setHwQuestion(prev => ({ ...prev, textbook: textbook }));
    }, [textbook]);

    useEffect(() => {
        setSelectedPage(page);
        setHwQuestion(prev => ({ ...prev, page: page }));
    }, [page]);

    useEffect(() => {
        setPdfNumPages(numPages);
    }, [numPages]);

    const questionTypes = [
        { label: "Multiple Choice", value: "multiple_choice" },
        { label: "Short Answer", value: "short_answer" },
        { label: "Long Answer", value: "long_answer" },
        { label: "File Upload", value: "file_upload" },
    ];

    const questionValidation = (question) => {
        if (question.textbook === null) {
            throw new Error("Please choose a textbook.");
        }
        if (question.page === null || question.page === 0) {
            throw new Error("Please choose a page.");
        }
        if (question.question_type === null) {
            throw new Error("Please choose a question type.");
        }
        if (question.question_label === "") {
            throw new Error("Please type in the question label.");
        }
        if (question.question_type === "multiple_choice" && question.options.length === 0) {
            throw new Error("Please add options.");
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // Build the question object
            const isCoordinateTool = chosenTool === "coordinate";
            const isTableTool = chosenTool === "table";
            const isNumberLineTool = chosenTool === "numberline";
            const isMappingTool = chosenTool === "mapping";
            const isPolynomialGraphTool = chosenTool === "polynomialGraph";
            const isRationalGraphTool = chosenTool === "rationalGraph";
            const isTrigGraphTool = chosenTool === "trigGraph";
            const isMultilineTool = chosenTool === "multiline";
            const isMathliveTool = chosenTool === "mathlive";
            const mathliveAllowsText = isMathliveTool && mathliveConfig.allowTextInput === true;

            // Determine answer based on tool type
            let answerValue = hwQuestion.answers;
            if (isCoordinateTool) {
                answerValue = [JSON.stringify(coordinateAnswer)];
            } else if (isTableTool) {
                answerValue = [JSON.stringify(extractAnswerFromConfig(tableConfig))];
            } else if (isNumberLineTool) {
                answerValue = [JSON.stringify(numberLineAnswer)];
            } else if (isMappingTool) {
                answerValue = [mappingAnswer];
            } else if (isPolynomialGraphTool) {
                answerValue = [JSON.stringify(polynomialGraphAnswer)];
            } else if (isRationalGraphTool) {
                answerValue = [JSON.stringify(rationalGraphAnswer)];
            } else if (isTrigGraphTool) {
                answerValue = [JSON.stringify(trigGraphAnswer)];
            } else if (isMultilineTool) {
                // Always persist admin's authored rows; for manual mode they
                // serve as the grader's reference answer.
                answerValue = [JSON.stringify(multilineAnswer)];
            } else if (mathliveAllowsText) {
                // Mixed math/text answers serialized as { type, value } per entry.
                // Math values still get LaTeX delimiters when renderAsLatex is on,
                // matching the legacy path below.
                answerValue = hwQuestion.answers.map((ans, i) => {
                    const type = mathliveAnswerTypes[i] === MATHLIVE_ANSWER_TYPES.TEXT
                        ? MATHLIVE_ANSWER_TYPES.TEXT
                        : MATHLIVE_ANSWER_TYPES.MATH;
                    const value = (type === MATHLIVE_ANSWER_TYPES.MATH && renderAsLatex)
                        ? wrapLatexDelimiters(ans)
                        : ans;
                    return stringifyMathliveAnswer({ type, value });
                });
            } else if (renderAsLatex && (chosenTool === 'mathlive' || chosenTool === 'textfield' || chosenTool === '')) {
                // Wrap answers with \(...\) delimiters if "Render as LaTeX" is checked
                answerValue = hwQuestion.answers.map(ans => {
                    if (!ans) return ans;
                    const trimmed = ans.trim();
                    // Check if already has delimiters
                    if (/^\\\(.*\\\)$/.test(trimmed) || /^\$.*\$$/.test(trimmed) || /^\\\[.*\\\]$/.test(trimmed)) {
                        return ans;
                    }
                    return `\\(${ans}\\)`;
                });
            }

            // Determine answer_config based on tool type
            let answerConfig = null;
            if (isCoordinateTool) {
                answerConfig = coordinateConfig;
            } else if (isTableTool) {
                answerConfig = tableConfig;
            } else if (isNumberLineTool) {
                answerConfig = numberLineConfig;
            } else if (isMappingTool) {
                answerConfig = mappingConfig;
            } else if (isPolynomialGraphTool) {
                answerConfig = polynomialGraphConfig;
            } else if (isRationalGraphTool) {
                answerConfig = rationalGraphConfig;
            } else if (isTrigGraphTool) {
                answerConfig = trigGraphConfig;
            } else if (isMultilineTool) {
                answerConfig = multilineConfig;
            } else if (isMathliveTool) {
                answerConfig = mathliveConfig;
            }

            const hw_question = {
                textbook_id: hwQuestion.textbook.id,
                page: hwQuestion.page,
                question_label: hwQuestion.question_label.trim(),
                content: hwQuestion.content || null,
                passage: hwQuestion.passage || null,
                options: hwQuestion.question_type === "multiple_choice" ? hwQuestion.options : null,
                question_type: hwQuestion.question_type,
                answer: answerValue,
                answer_tool: (hwQuestion.question_type === 'short_answer' || hwQuestion.question_type === 'long_answer')
                    ? (chosenTool === "" ? 'textfield' : chosenTool)
                    : null,
                grading_type: hwQuestion.question_type === "long_answer" ? gradingType : null,
                answer_config: answerConfig,
            };

            // Validate
            questionValidation(hw_question);

            // Submit to API
            const response = await Axios.post(`${process.env.REACT_APP_URL}/api/v1/hw-questions`, {
                ...hw_question,
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            });

            // Reset form
            initiateVars();

            // Call success callback
            if (onSubmit) {
                onSubmit(response.data);
            }
        } catch (error) {
            console.error("Error creating question:", error);
            throw error; // Re-throw to let parent handle the error
        } finally {
            setSubmitting(false);
        }
    };

    const initiateVars = () => {
        setChosenQuestionType(null);
        setOptions([]);
        setQuestionLabel("");
        setAnswers([]);
        setChosenTool("");
        setNumAnswers(1);
        setGradingType(null);
        setCoordinateConfig(defaultCoordinateConfig);
        setCoordinateAnswer({ elements: [] });
        setTableConfig(defaultTableConfig);
        setTableAnswer({ rows: [] });
        setNumberLineConfig(defaultNumberLineConfig);
        setNumberLineAnswer({ elements: [] });
        setMappingConfig(defaultMappingConfig);
        setMappingAnswer('');
        setPolynomialGraphConfig(defaultPolynomialGraphConfig);
        setPolynomialGraphAnswer({ correctGraphIndex: 0 });
        setRationalGraphConfig(defaultRationalGraphConfig);
        setRationalGraphAnswer({ correctGraphIndex: 0 });
        setTrigGraphConfig(defaultTrigGraphConfig);
        setTrigGraphAnswer({ correctGraphIndex: 0 });
        setMathliveConfig(defaultMathliveConfig);
        setMathliveAnswerTypes([]);
        setHwQuestion({
            textbook: textbook || chosenTextbook,
            page: page || selectedPage,
            question_type: null,
            question_label: "",
            content: "",
            passage: "",
            options: [],
            answers: [],
            answer_tool: "",
            grading_type: null,
        });
    };

    function onDocumentLoadSuccess({ numPages }) {
        setPdfNumPages(numPages);
        setPdfLoading(false);
    }

    const documentRender = () => {
        if (!chosenTextbook?.file_url) return null;

        return (
            <Document
                file={chosenTextbook.file_url}
                options={pdfOptions}
                onLoadSuccess={onDocumentLoadSuccess}
                onContextMenu={(e) => e.preventDefault()}
                className="pdf-container"
                loading={
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress color="inherit" size={40} />
                    </Box>
                }
            >
                <div>
                    <Page
                        pageNumber={selectedPage || 1}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="react-pdf__Page__canvas"
                        loading={
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress color="inherit" size={40} />
                            </Box>
                        }
                    />
                </div>
            </Document>
        );
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "row", gap: "20px", justifyContent: "flex-start", alignItems: "flex-start" }}>
            {/* Form Section */}
            <Box
                sx={{
                    width: showPDF ? "46%" : "100%",
                }}
                padding={compact ? 2 : 4}
                boxShadow={compact ? 0 : 3}
                borderRadius={compact ? 0 : 3}
                m={compact ? 0 : 1}
                bgcolor={compact ? 'transparent' : colors.primary[600]}
            >
                <Typography variant="h4" fontWeight="bold" mb={3}>
                    Question Details
                </Typography>

                <Box display="grid" gap="30px" gridTemplateColumns="repeat(4, minmax(0, 1fr))">
                    {/* Textbook Selector */}
                    {showTextbookSelector && (
                        <Autocomplete
                            disablePortal
                            id="textbook-selector"
                            options={textbooks}
                            sx={{ gridColumn: "span 4" }}
                            value={chosenTextbook}
                            renderInput={(params) => <TextField {...params} label="1. Choose Textbook" />}
                            onChange={(event, newValue) => {
                                setChosenTextbook(newValue);
                                setHwQuestion({ ...hwQuestion, textbook: newValue });
                                if (newValue) {
                                    setPdfLoading(true);
                                }
                            }}
                        />
                    )}

                    {/* Page Selector */}
                    {showPageSelector && (
                        <FormControl
                            disabled={chosenTextbook === null || pdfLoading}
                            sx={{
                                minWidth: 120,
                                maxWidth: 300,
                                gridColumn: "span 2",
                                position: 'relative',
                            }}
                        >
                            {pdfLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '20px',
                                        height: '20px',
                                        zIndex: 1000,
                                    }}
                                >
                                    <CircularProgress color="inherit" size={20} />
                                </Box>
                            )}
                            <InputLabel id="page-selector-label">2. Choose page</InputLabel>
                            <Select
                                labelId="page-selector-label"
                                id="page-selector"
                                value={selectedPage}
                                label="Page"
                                onChange={(event) => {
                                    setSelectedPage(event.target.value);
                                    setHwQuestion({ ...hwQuestion, page: event.target.value });
                                }}
                            >
                                <MenuItem value={0}>
                                    <em>None</em>
                                </MenuItem>
                                {Array(pdfNumPages).fill(0).map((_, index) => (
                                    <MenuItem key={index} value={index + 1}>{index + 1}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Question Label */}
                    <TextField
                        disabled={chosenTextbook === null || selectedPage === 0}
                        variant="filled"
                        type="text"
                        label={showTextbookSelector || showPageSelector ? "3. Type in the Question Label" : "Question Label"}
                        onChange={(e) => {
                            setQuestionLabel(e.target.value);
                            setHwQuestion({ ...hwQuestion, question_label: e.target.value });
                        }}
                        name="question-label"
                        value={questionLabel}
                        sx={{ gridColumn: showPageSelector ? "span 2" : "span 4" }}
                    />

                    {/* Passage Field */}
                    <TextFieldWithMath
                        label="Passage (Optional)"
                        value={hwQuestion.passage}
                        onChange={(value) => {
                            setHwQuestion({ ...hwQuestion, passage: value });
                        }}
                        disabled={chosenTextbook === null || selectedPage === 0}
                        multiline
                        rows={3}
                        helperText="Shared context or reference text for this question (click ƒ button to insert math)"
                        sx={{ gridColumn: "span 4" }}
                    />

                    {/* Question Content Field */}
                    <TextFieldWithMath
                        label="Question Content"
                        value={hwQuestion.content}
                        onChange={(value) => {
                            setHwQuestion({ ...hwQuestion, content: value });
                        }}
                        disabled={chosenTextbook === null || selectedPage === 0}
                        multiline
                        rows={4}
                        required
                        sx={{ gridColumn: "span 4" }}
                    />

                    {/* Question Type Selector */}
                    <Autocomplete
                        disabled={chosenTextbook === null || selectedPage === 0}
                        disablePortal
                        id="question-type-selector"
                        options={questionTypes}
                        sx={{ gridColumn: "span 2" }}
                        renderInput={(params) => <TextField {...params} label="4. Choose Question Type" />}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        onChange={(event, newValue) => {
                            setAnswers([]);
                            setOptions([]);
                            // Reset answer tool so a stale tool (e.g. numberline)
                            // doesn't hijack the save path with its JSON blob.
                            setChosenTool("");
                            setChosenQuestionType(newValue?.value);
                            setNumAnswers(1);

                            // Auto-detect grading type from textbook title for long_answer
                            let detectedGradingType = null;
                            if (newValue?.value === 'long_answer' && chosenTextbook?.title) {
                                const title = chosenTextbook.title.toLowerCase();
                                if (title.includes('apush') || title.includes('ap us history')) {
                                    detectedGradingType = 'apush_frq';
                                } else if (title.includes('middle science')) {
                                    detectedGradingType = 'middle_science_frq';
                                }
                            }
                            setGradingType(detectedGradingType);
                            setHwQuestion({
                                ...hwQuestion,
                                question_type: newValue?.value,
                                answers: [],
                                options: [],
                                grading_type: detectedGradingType,
                            });
                        }}
                    />

                    {/* Grading Type Selector - Show only for long_answer */}
                    {chosenQuestionType === "long_answer" && (
                        <FormControl sx={{ gridColumn: "span 2" }}>
                            <InputLabel id="grading-type-label">Grading Type</InputLabel>
                            <Select
                                labelId="grading-type-label"
                                id="grading-type-selector"
                                value={gradingType || ""}
                                label="Grading Type"
                                onChange={(e) => {
                                    const value = e.target.value || null;
                                    setGradingType(value);
                                    setHwQuestion({ ...hwQuestion, grading_type: value });
                                }}
                            >
                                <MenuItem value="">
                                    <em>Manual Grading</em>
                                </MenuItem>
                                {GRADING_TYPES.map((gt) => (
                                    <MenuItem key={gt.value} value={gt.value}>{gt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>

                {/* Multiple Choice Options */}
                {chosenQuestionType === "multiple_choice" && (
                    <Box mt={2} sx={{ display: "flex", gap: "20px", justifyContent: "flex-start", alignItems: "flex-start", flexDirection: "column" }}>
                        <Typography variant="h6" fontWeight="bold">
                            Options (ex: A., B., C., D. or 1., 2., 3., 4., 1), 2), etc.)
                        </Typography>
                        <Box display='flex' justifyContent='start' alignItems='flex-start' flexWrap='wrap' gap={2}>
                            {options.map((option, index) => (
                                <Box position="relative" key={index} sx={{ maxWidth: "400px", width: "100%" }}>
                                    <TextFieldWithMath
                                        label={`Option ${index + 1}`}
                                        value={option}
                                        onChange={(value) => {
                                            setOptions(options.map((opt, i) => i === index ? value : opt));
                                            setHwQuestion({
                                                ...hwQuestion,
                                                options: options.map((opt, i) => i === index ? value : opt),
                                                answers: [],
                                            });
                                            setAnswers([]);
                                        }}
                                        multiline
                                        rows={2}
                                    />
                                    <CloseIcon
                                        sx={{
                                            position: 'absolute',
                                            right: -8,
                                            top: -8,
                                            color: colors.redAccent[400],
                                            cursor: 'pointer',
                                            zIndex: 1000,
                                            backgroundColor: colors.primary[400],
                                            borderRadius: '50%',
                                            padding: '2px',
                                        }}
                                        fontSize="small"
                                        onClick={() => {
                                            setOptions(options.filter((_, i) => i !== index));
                                            setHwQuestion({
                                                ...hwQuestion,
                                                options: options.filter((_, i) => i !== index),
                                                answers: [],
                                            });
                                            setAnswers([]);
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                        <Box display='flex' justifyContent='start' alignItems='center' flexDirection='column' gap={2}>
                            <Button type="button" color="primary" variant="contained" onClick={() => setOptions([...options, ''])}>
                                Add Option
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Answer Selection/Input */}
                {chosenQuestionType !== null && (
                    <>
                        <Box mt={2} sx={{ display: "flex", gap: "20px", justifyContent: "flex-start", alignItems: "flex-start" }}>
                            {chosenQuestionType === "multiple_choice" ? (
                                <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
                                    <InputLabel id="answer-selector-label">Choose answer(s) - by index</InputLabel>
                                    <Select
                                        labelId="answer-selector-label"
                                        id="answer-selector"
                                        multiple
                                        value={answers}
                                        label="Answer(s) - by index"
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            // Store indices (numbers) for MC answers
                                            const indices = typeof value === 'string' ? value.split(',').map(Number) : value;
                                            setAnswers(indices);
                                            setHwQuestion({
                                                ...hwQuestion,
                                                answers: indices
                                            });
                                        }}
                                    >
                                        {options.map((option, index) => (
                                            <MenuItem key={index} value={index}>[{index}] {option}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : chosenQuestionType === "short_answer" || chosenQuestionType === "long_answer" ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
                                        <InputLabel id="tool-selector-label">Choose a answer tool</InputLabel>
                                        <Select
                                            labelId="tool-selector-label"
                                            id="tool-selector"
                                            value={chosenTool}
                                            label="Answer"
                                            onChange={(event) => {
                                                setChosenTool(event.target.value);
                                                setAnswers([]);
                                                setHwQuestion({
                                                    ...hwQuestion,
                                                    answer_tool: event.target.value,
                                                });
                                                setNumAnswers(1);
                                                // Auto-check "Render as LaTeX" when mathlive is selected
                                                if (event.target.value === 'mathlive') {
                                                    setRenderAsLatex(true);
                                                }
                                            }}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {ANSWER_TOOLS.map((tool) => (
                                                <MenuItem key={tool.value} value={tool.value}>{tool.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Box display="flex" justifyContent="start" alignItems="center" flexDirection="column" gap={2}>
                                        {chosenQuestionType === "short_answer" && chosenTool === "textfield" ? (
                                            Array(numAnswers).fill(0).map((_, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: "flex",
                                                        gap: "10px",
                                                        alignItems: "flex-start",
                                                        width: "100%",
                                                        position: "relative",
                                                    }}
                                                >
                                                    <TextField
                                                        disabled={chosenQuestionType === "long_answer" || chosenQuestionType === "essay"}
                                                        variant="filled"
                                                        type="text"
                                                        label="6. Add Answer"
                                                        onChange={(e) => {
                                                            const oldValue = hwQuestion.answers[index] || '';
                                                            let temp = hwQuestion.answers;
                                                            temp[index] = e.target.value;
                                                            setAnswers(temp);
                                                            setHwQuestion({
                                                                ...hwQuestion,
                                                                answers: temp,
                                                            });

                                                            // If user removed LaTeX delimiters, uncheck renderAsLatex
                                                            const hadDelimiters = /\\\(|\\\)|\$/.test(oldValue);
                                                            const hasDelimiters = /\\\(|\\\)|\$/.test(e.target.value);
                                                            if (hadDelimiters && !hasDelimiters) {
                                                                setRenderAsLatex(false);
                                                            }
                                                        }}
                                                        name="answer"
                                                        value={answers[index]}
                                                        sx={{ minWidth: "300px", maxWidth: "400px" }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '12px',
                                                            right: -30,
                                                            color: colors.redAccent[400],
                                                            zIndex: 1000,
                                                        }}
                                                    >
                                                        <CloseIcon
                                                            color='error'
                                                            sx={{
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    color: colors.redAccent[600]
                                                                }
                                                            }}
                                                            onClick={() => {
                                                                setAnswers(hwQuestion.answers.filter((_, i) => i !== index));
                                                                setHwQuestion({
                                                                    ...hwQuestion,
                                                                    answers: hwQuestion.answers.filter((_, i) => i !== index),
                                                                });
                                                                setNumAnswers(numAnswers - 1);
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            ))
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "mathlive" ? (
                                            Array(numAnswers).fill(0).map((_, index) => {
                                                const answerType = mathliveAnswerTypes[index] === MATHLIVE_ANSWER_TYPES.TEXT
                                                    ? MATHLIVE_ANSWER_TYPES.TEXT
                                                    : MATHLIVE_ANSWER_TYPES.MATH;
                                                const removeAnswer = () => {
                                                    const nextAnswers = hwQuestion.answers.filter((_, i) => i !== index);
                                                    setAnswers(nextAnswers);
                                                    setHwQuestion({ ...hwQuestion, answers: nextAnswers });
                                                    setMathliveAnswerTypes((prev) => prev.filter((_, i) => i !== index));
                                                    setNumAnswers(numAnswers - 1);
                                                };
                                                return (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "10px",
                                                            alignItems: "flex-start",
                                                            width: "100%",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {mathliveConfig.allowTextInput && (
                                                            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                                                                {answerType === MATHLIVE_ANSWER_TYPES.TEXT ? 'Text answer' : 'Math answer'}
                                                            </Typography>
                                                        )}
                                                        {answerType === MATHLIVE_ANSWER_TYPES.TEXT ? (
                                                            <TextField
                                                                variant="filled"
                                                                type="text"
                                                                label='Text Answer (e.g. "no solutions")'
                                                                value={answers[index] || ''}
                                                                onChange={(e) => {
                                                                    const temp = [...hwQuestion.answers];
                                                                    temp[index] = e.target.value;
                                                                    setAnswers(temp);
                                                                    setHwQuestion({ ...hwQuestion, answers: temp });
                                                                }}
                                                                sx={{ minWidth: "300px", maxWidth: "400px" }}
                                                            />
                                                        ) : (
                                                            <>
                                                                <math-field
                                                                    id="mathfield"
                                                                    onInput={evt => {
                                                                        const oldValue = hwQuestion.answers[index] || '';
                                                                        let temp = hwQuestion.answers;
                                                                        temp[index] = evt.target.value;
                                                                        setAnswers(temp);
                                                                        setHwQuestion({
                                                                            ...hwQuestion,
                                                                            answers: temp,
                                                                        });

                                                                        // If user removed LaTeX delimiters, uncheck renderAsLatex
                                                                        if (hasLatexDelimiters(oldValue) && !hasLatexDelimiters(evt.target.value)) {
                                                                            setRenderAsLatex(false);
                                                                        }
                                                                    }}
                                                                    style={{ width: "inherit", minWidth: "300px", maxWidth: "400px" }}
                                                                />
                                                                <p>Answer: {answers[index]}</p>
                                                            </>
                                                        )}
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '12px',
                                                                right: -30,
                                                                color: colors.redAccent[400],
                                                                zIndex: 1000,
                                                            }}
                                                        >
                                                            <CloseIcon
                                                                color='error'
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        color: colors.redAccent[600]
                                                                    }
                                                                }}
                                                                onClick={removeAnswer}
                                                            />
                                                        </Box>
                                                    </Box>
                                                );
                                            })
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "tinymce" ? (
                                            <Box></Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "coordinate" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <CoordinateGridAdmin
                                                    config={coordinateConfig}
                                                    onConfigChange={setCoordinateConfig}
                                                    correctAnswer={coordinateAnswer}
                                                    onAnswerChange={setCoordinateAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "table" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <TableAdmin
                                                    config={tableConfig}
                                                    onConfigChange={setTableConfig}
                                                    correctAnswer={tableAnswer}
                                                    onAnswerChange={setTableAnswer}
                                                />

                                                {/* Table Answer Validation Logic Summary - helps admin preview grading rules */}
                                                <TableAnswerLogicSummary
                                                    config={tableConfig}
                                                    compact={true}
                                                    colors={colors}
                                                    mode="full"
                                                    title="Grading Rules Preview"
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "numberline" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <NumberLineAdmin
                                                    config={numberLineConfig}
                                                    onConfigChange={setNumberLineConfig}
                                                    correctAnswer={numberLineAnswer}
                                                    onAnswerChange={setNumberLineAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "mapping" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <MappingAdmin
                                                    config={mappingConfig}
                                                    answer={mappingAnswer}
                                                    onConfigChange={setMappingConfig}
                                                    onAnswerChange={setMappingAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "polynomialGraph" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <PolynomialGraphAdmin
                                                    config={polynomialGraphConfig}
                                                    onConfigChange={setPolynomialGraphConfig}
                                                    correctAnswer={polynomialGraphAnswer}
                                                    onAnswerChange={setPolynomialGraphAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "rationalGraph" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <RationalGraphAdmin
                                                    config={rationalGraphConfig}
                                                    onConfigChange={setRationalGraphConfig}
                                                    correctAnswer={rationalGraphAnswer}
                                                    onAnswerChange={setRationalGraphAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "trigGraph" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <TrigGraphAdmin
                                                    config={trigGraphConfig}
                                                    onConfigChange={setTrigGraphConfig}
                                                    correctAnswer={trigGraphAnswer}
                                                    onAnswerChange={setTrigGraphAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "multiline" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <MultilineAdmin
                                                    config={multilineConfig}
                                                    onConfigChange={setMultilineConfig}
                                                    correctAnswer={multilineAnswer}
                                                    onAnswerChange={setMultilineAnswer}
                                                />
                                            </Box>
                                        ) : null}
                                    </Box>
                                </Box>
                            ) : null}
                        </Box>
                        <Box mt={2} sx={{ display: "flex", gap: "20px", justifyContent: "flex-start", alignItems: "center", flexWrap: "wrap" }}>
                            {chosenQuestionType === "short_answer" && chosenTool === "textfield" && (
                                <>
                                    <Button
                                        type="button"
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => {
                                            setNumAnswers(numAnswers + 1);
                                            setAnswers([...answers, '']);
                                            setHwQuestion({
                                                ...hwQuestion,
                                                answers: [...hwQuestion.answers, ''],
                                            });
                                        }}
                                    >
                                        Add Answer
                                    </Button>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={renderAsLatex}
                                            onChange={(e) => setRenderAsLatex(e.target.checked)}
                                            size="small"
                                        />
                                        <Typography variant="body2">Render as LaTeX</Typography>
                                    </Box>
                                </>
                            )}
                            {chosenQuestionType === "short_answer" && chosenTool === "mathlive" && (
                                <>
                                    <Button
                                        type="button"
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => {
                                            setNumAnswers(numAnswers + 1);
                                            setAnswers([...answers, '']);
                                            setHwQuestion({
                                                ...hwQuestion,
                                                answers: [...hwQuestion.answers, ''],
                                            });
                                            setMathliveAnswerTypes((prev) => [...prev, MATHLIVE_ANSWER_TYPES.MATH]);
                                        }}
                                    >
                                        {mathliveConfig.allowTextInput ? 'Add Math Answer' : 'Add Answer'}
                                    </Button>
                                    {mathliveConfig.allowTextInput && (
                                        <Button
                                            type="button"
                                            color="info"
                                            variant="contained"
                                            onClick={() => {
                                                setNumAnswers(numAnswers + 1);
                                                setAnswers([...answers, '']);
                                                setHwQuestion({
                                                    ...hwQuestion,
                                                    answers: [...hwQuestion.answers, ''],
                                                });
                                                setMathliveAnswerTypes((prev) => [...prev, MATHLIVE_ANSWER_TYPES.TEXT]);
                                            }}
                                        >
                                            Add Text Answer
                                        </Button>
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={renderAsLatex}
                                            onChange={(e) => setRenderAsLatex(e.target.checked)}
                                            size="small"
                                        />
                                        <Typography variant="body2">Render as LaTeX</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={mathliveConfig.allowTextInput}
                                            onChange={(e) => setMathliveConfig({ ...mathliveConfig, allowTextInput: e.target.checked })}
                                            size="small"
                                        />
                                        <Typography variant="body2">
                                            Allow text answer (e.g. "no solutions", "infinitely many")
                                        </Typography>
                                    </Box>
                                    <MathAnswerSpecEditor
                                        questionContent={hwQuestion.content}
                                        passage={hwQuestion.passage}
                                        config={mathliveConfig}
                                        onChange={setMathliveConfig}
                                    />
                                </>
                            )}
                        </Box>
                    </>
                )}

                {/* Submit Button */}
                <Box display="flex" justifyContent="start" gap={2} mt={4}>
                    <Button
                        type="button"
                        color="secondary"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? 'Creating...' : 'Create New Homework Question'}
                    </Button>
                    {onCancel && (
                        <Button
                            type="button"
                            color="primary"
                            variant="outlined"
                            onClick={onCancel}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                    )}
                </Box>
            </Box>

            {/* PDF Viewer (if showPDF is true) */}
            {showPDF && chosenTextbook && (
                <Box
                    sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        width: "50%",
                        height: "100%",
                    }}
                >
                    <HiddenPDF documentRender={documentRender} isHidden={false} />
                </Box>
            )}
        </Box>
    );
};

export default HwQuestionForm;
